// ui/useCardPlayAnimation.js: M5 core-game-polish C4.
//
// rAF-driven slide overlay for newly-played cards. Subscribes to the
// snapshot stream via createCardPlayDetector and, for the most recent
// play, returns a `slide` object with the interpolated current position
// each frame. The renderer draws the sliding card at that position
// instead of at the static trick slot for the duration.
//
// One slide at a time, by design. If a second play lands while one is
// still mid-slide (rare: a bot waits for the 100ms slide before its
// 200ms think, and a human rarely taps within 100ms of a card)
// the new play preempts the old one. The
// previous card is already drawn at the static slot via the snapshot,
// so the visual loss is just the latter half of an animation, not a
// missing card.
//
// Reduced-motion: when `reducedMotion` is true the slide collapses to
// zero duration, the hook returns null and the renderer falls back
// to the static snapshot path. Matches the easing.js *OrReduced sibling
// convention (Decision B1).

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createCardPlayDetector } from './createCardPlayDetectorOrReduced.js';
import { easeOut } from './easing.js';

const SLIDE_DURATION_MS = 100;

function nowMs() {
  if (typeof performance !== 'undefined' && performance.now) return performance.now();
  return Date.now();
}

/**
 * @typedef {{
 *   seat: 1|2|3|4,
 *   card_id: string,
 *   t: number,          // 0..1 eased progress
 *   elapsedMs: number,
 *   durationMs: number,
 * }} CardSlideOverlay
 */

/**
 * Watch the snapshot for newly-played cards. When one lands, drive a
 * rAF clock for `durationMs` and report progress as a normalized t
 * (0..1, eased). Renderer combines `t` with layout-derived from/to
 * coords to draw the card mid-flight.
 *
 * @param {{
 *   snapshot: any,
 *   reducedMotion?: boolean,
 *   durationMs?: number,
 * }} opts
 * @returns {CardSlideOverlay | null}
 */
export function useCardPlayAnimation(opts) {
  const {
    snapshot,
    reducedMotion = false,
    durationMs = SLIDE_DURATION_MS,
  } = opts;

  const detectorRef = useRef(/** @type {ReturnType<typeof createCardPlayDetector>|null} */ (null));
  if (detectorRef.current === null) {
    detectorRef.current = createCardPlayDetector({ durationMs });
  }

  // The slide in flight, or null. Detection runs during render, the
  // moment a new snapshot arrives, so the frame that first draws a new
  // card already draws it at the start of its slide. (Detecting in an
  // effect drew the card at rest in its slot for one frame, then jumped
  // it back to the seat to slide in.)
  const activeRef = useRef(
    /** @type {null | { seat:number, card_id:string, startMs:number, durationMs:number }} */ (null),
  );
  const elapsedRef = useRef(0);
  const seenSnapshotRef = useRef(/** @type {any} */ (undefined));
  const [, setTick] = useState(0);
  const rafRef = useRef(/** @type {number|null} */ (null));

  if (snapshot !== seenSnapshotRef.current) {
    seenSnapshotRef.current = snapshot;
    const events = detectorRef.current?.detect(snapshot) ?? [];
    if (events.length > 0) {
      // Reduced motion or a zero duration: no slide (the renderer falls
      // back to the static snapshot path). Otherwise use the LATEST event,
      // preempting any in-flight slide.
      const ev = events[events.length - 1];
      activeRef.current = (reducedMotion || durationMs <= 0)
        ? null
        : { seat: ev.seat, card_id: ev.card_id, startMs: nowMs(), durationMs };
      elapsedRef.current = 0;
    }
  }
  const active = activeRef.current;

  // rAF loop: advance the slide while one is active.
  useEffect(() => {
    if (!active) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    const tick = (now) => {
      if (activeRef.current !== active) return;
      const elapsed = Math.min(active.durationMs, now - active.startMs);
      elapsedRef.current = elapsed;
      if (elapsed >= active.durationMs) {
        rafRef.current = null;
        activeRef.current = null;
        setTick((n) => n + 1);
        return;
      }
      setTick((n) => n + 1);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [active]);

  const elapsedMs = elapsedRef.current;
  // Memoize the returned overlay so the reference is stable across
  // renders that don't change the animation state. Without this, every
  // parent re-render (e.g. caused by setPlayerHits inside the canvas
  // useEffect) would yield a fresh `slideOverlay` object, retriggering
  // any caller `useEffect` that has `slideOverlay` in its deps, a
  // self-sustaining render loop during the 100ms slide. See review
  // log core-game-polish-3of5 follow-up.
  return useMemo(() => {
    if (!active) return null;
    const t = active.durationMs > 0
      ? easeOut(Math.min(1, elapsedMs / active.durationMs))
      : 1;
    /** @type {CardSlideOverlay} */
    const overlay = {
      seat: /** @type {1|2|3|4} */ (active.seat),
      card_id: active.card_id,
      t,
      elapsedMs,
      durationMs: active.durationMs,
    };
    return overlay;
  }, [active, elapsedMs]);
}

export const _internals = {
  SLIDE_DURATION_MS,
};
